import { ApolloClient, ApolloProvider, createHttpLink, defaultDataIdFromObject, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import axios from "axios";
import { createContext, FC, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMeQuery, useUpdateUserMutation } from "@/graphql";
import introspection from "@/graphql";
import { LANGUAGE_OPTIONS } from "@/app/languages";

export type UserRole = "admin" | "creator" | "user";
export type UserGroup = "key" | "stylist" | "consumer" | "internal" | "external" | "super";

type FinishRegistrationCredentials = {
  accessToken: string;
  firstName: string;
  lastName: string;
  country: string;
  role?: string;
  salonName?: string;
};

type FinishRegistrationResponseType = {
  successfully: boolean;
};

type SetPasswordCredentials = {
  accessToken: string;
  mode: "forgot-password" | "registration" | "reset-password";
  password: string;
  currentPassword?: string;
};

type SetPasswordResponseType = {
  set: boolean;
};

type SendEmailConfirmationCredentials = {
  email: string;
};

type SendEmailConfirmationResponseType = {
  email: string;
  sent: boolean;
};

type ConfirmEmailCredentials = {
  email: string;
  code: number;
};

type ConfirmEmailResponseType = {
  accessToken: string;
  refreshToken: string;
};

type LoginCredentialsType = {
  identifier: string;
  password: string;
};

type LoginResponseType = {
  accessToken: string;
  refreshToken: string;
  user: UsersPermissionsUser;
};

type InviteUserResponseType = {
  user: UsersPermissionsUser;
};

export type UserCMSRole =
  | "super_admin"
  | "consumer"
  | "internal_admin"
  | "external_admin"
  | "internal_creator"
  | "external_creator"
  | "regular_stylist"
  | "key_stylist"
  | "internal_user";

export const getRole = (role: UserRole, group: UserGroup): UserCMSRole => {
  if (role === "admin") {
    if (group === "internal") return "internal_admin";
    if (group === "external") return "external_admin";
    if (group === "super") return "super_admin";
    throw new Error(`Invalid group "${group}" for role "admin"`);
  }

  if (role === "creator") {
    if (group === "internal") return "internal_creator";
    if (group === "external") return "external_creator";
    throw new Error(`Invalid group "${group}" for role "creator"`);
  }

  if (role === "user") {
    if (group === "key") return "key_stylist";
    if (group === "stylist") return "regular_stylist";
    if (group === "consumer") return "consumer";
    if (group === "internal") return "internal_user";
    throw new Error(`Invalid group "${group}" for role "user"`);
  }

  throw new Error(`Unsupported role: ${role}`);
};

export const getRoleAndGroup = (parsedRole: UserCMSRole): { role: UserRole; group: UserGroup } => {
  switch (parsedRole) {
    case "internal_admin":
      return { role: "admin", group: "internal" };
    case "external_admin":
      return { role: "admin", group: "external" };

    case "internal_creator":
      return { role: "creator", group: "internal" };
    case "external_creator":
      return { role: "creator", group: "external" };

    case "key_stylist":
      return { role: "user", group: "key" };
    case "regular_stylist":
      return { role: "user", group: "stylist" };
    case "consumer":
      return { role: "user", group: "consumer" };
    case "internal_user":
      return { role: "user", group: "internal" };
    case "super_admin":
      return { role: "admin", group: "super" };

    default:
      throw new Error(`Unsupported parsed role: ${parsedRole}`);
  }
};

export type InviteUserCredentialsType = {
  accessToken: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserCMSRole;
  country: string;
  avatar?: Maybe<string>;
};

const url = import.meta.env.VITE_API_URL;

export const api = axios.create({
  baseURL: `${url || ""}/api`,
});

const getNewToken = async () => {
  const response = await fetch(`${url || ""}/api/token/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      refreshToken: localStorage.getItem("refreshToken"),
    }),
  });

  const data = await response.json();

  if (!data?.accessToken || !data?.refreshToken) {
    console.error(new Error("Failed to refresh token"));

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    return null;
  }

  localStorage.setItem("accessToken", data.accessToken);
  localStorage.setItem("refreshToken", data.refreshToken);

  return data.accessToken;
};

const httpLink = createHttpLink({
  uri: `${url || ""}/graphql`,
  credentials: "same-origin",
});

const authLink = setContext(async (_, { headers }) => {
  if (headers?.authorization) {
    return { headers };
  }

  let accessToken = localStorage.getItem("accessToken");
  const refreshToken = localStorage.getItem("refreshToken");

  if (!accessToken && refreshToken) {
    accessToken = await getNewToken();
  }

  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : "",
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const isUnauthorized = graphQLErrors?.some(({ extensions }) => extensions?.code === "FORBIDDEN") || networkError?.message?.includes("401");

  if (isUnauthorized) {
    localStorage.removeItem("accessToken");
  }

  return forward(operation);
});

const client = new ApolloClient({
  link: errorLink.concat(authLink.concat(httpLink)),
  connectToDevTools: import.meta.env.DEV,
  queryDeduplication: true,
  assumeImmutableResults: true,
  defaultOptions: {
    watchQuery: {
      notifyOnNetworkStatusChange: true,
    },
  },
  cache: new InMemoryCache({
    resultCaching: import.meta.env.PROD,
    possibleTypes: introspection.possibleTypes,
    dataIdFromObject: (object) => (object.__typename?.startsWith("Localized") ? undefined : defaultDataIdFromObject(object)),

    typePolicies: {
      Post: {
        keyFields: ["documentId"],
      },
      PostComment: {
        keyFields: ["documentId"],
      },
      Query: {
        fields: {
          post: {
            read: (existing, { args, toReference }) => existing ?? toReference({ __typename: "Post", documentId: args?.documentId }),
          },
          postComments_connection: {
            keyArgs: ["filters"],
            merge(existing = { nodes: [] }, incoming, { args }) {
              if (args?.pagination?.page === 1) {
                return incoming;
              }
              return {
                ...incoming,
                nodes: [...(existing.nodes || []), ...(incoming.nodes || [])],
              };
            },
          },
        },
      },
    },
  }),
});

type AuthContextType = {
  login: (credentials: LoginCredentialsType) => Promise<Omit<LoginResponseType, "refreshToken"> | null>;
  sendEmailConfirmation: (credentials: SendEmailConfirmationCredentials) => Promise<boolean>;
  confirmEmail: (credentials: ConfirmEmailCredentials) => Promise<string | null>;
  setPassword: (credentials: SetPasswordCredentials) => Promise<boolean>;
  finishRegistration: (credentials: FinishRegistrationCredentials) => Promise<boolean>;
  inviteUser: (credentials: InviteUserCredentialsType) => Promise<boolean>;
  setAvatar: (avatar: string | null) => void;
  avatar: string | null;
};

type AuthProviderProps = PropsWithChildren<Partial<AuthContextType>>;

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

const sendEmailConfirmation = async ({ email }: SendEmailConfirmationCredentials): Promise<boolean> => {
  const { data } = await api.post<SendEmailConfirmationResponseType>("/auth/send-email-confirmation", {
    email,
  });

  return !!data?.email && !!data?.sent;
};

const confirmEmail = async ({ email, code }: ConfirmEmailCredentials): Promise<string | null> => {
  const { data } = await api.get<ConfirmEmailResponseType>(`/auth/email-confirmation?email=${encodeURIComponent(email)}&code=${code}`);

  if (data?.accessToken) {
    return data.accessToken;
  }

  return null;
};

const setPassword = async ({ accessToken, mode, password, currentPassword }: SetPasswordCredentials): Promise<boolean> => {
  const { data } = await api.post<SetPasswordResponseType>(
    "auth/set-password",
    { mode, password, currentPassword },
    {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return !!data?.set;
};

const finishRegistration = async ({ accessToken, firstName, lastName, country, role, salonName }: FinishRegistrationCredentials): Promise<boolean> => {
  const { data } = await api.post<FinishRegistrationResponseType>(
    "auth/finish-registration",
    { firstName, lastName, country, role, salonName },
    {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return !!data?.successfully;
};

const inviteUser = async ({ accessToken, ...args }: InviteUserCredentialsType): Promise<boolean> => {
  const { data } = await api.post<InviteUserResponseType>("auth/local/invite", args, {
    headers: {
      authorization: `Bearer ${accessToken}`,
    },
  });

  return !!data?.user;
};

const login = async ({ identifier, password }: LoginCredentialsType): Promise<Omit<LoginResponseType, "refreshToken"> | null> => {
  const { data } = await api.post<LoginResponseType>("/auth/local", {
    identifier,
    password,
  });

  if (data?.accessToken && data?.refreshToken) {
    localStorage.setItem("accessToken", data.accessToken);
    localStorage.setItem("refreshToken", data.refreshToken);
    localStorage.setItem("role", data.user.role?.name || "Consumer");
    localStorage.setItem("userDocumentId", data.user.documentId);
    localStorage.setItem("avatar", data.user.avatar?.url || "");
    localStorage.setItem("lastPing", Date.now().toString());

    return { accessToken: data.accessToken, user: data.user };
  }

  return null;
};

export const LOGIN_LANGUAGE_KEY = "loginLanguage";

export const logout = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("role");
  location.reload();
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [avatar, setAvatar] = useState<string | null>(null);

  return (
    <ApolloProvider client={client}>
      <AuthProviderInner avatar={avatar} setAvatar={setAvatar}>
        {children}
      </AuthProviderInner>
    </ApolloProvider>
  );
};

const AuthProviderInner: FC<{ avatar: string | null; setAvatar: (a: string | null) => void; children: React.ReactNode }> = ({
  children,
  avatar,
  setAvatar,
}) => {
  const { data } = useMeQuery();
  const { i18n } = useTranslation();
  const [updateUser] = useUpdateUserMutation();
  const syncedUserIdRef = useRef<string | null>(null);
  const handleChangeAvatar = (newAvatar: string | null) => setAvatar(newAvatar);

  useEffect(() => {
    if (data?.me?.avatar?.url) setAvatar(data.me.avatar.url);
  }, [data, setAvatar]);

  useEffect(() => {
    const me = data?.me;

    if (!me?.id || syncedUserIdRef.current === me.id) return;

    syncedUserIdRef.current = me.id;

    const currentLanguage = LANGUAGE_OPTIONS.find((option) => option.value === (i18n.resolvedLanguage || i18n.language).slice(0, 2))?.value;
    const loginLanguage = LANGUAGE_OPTIONS.find((option) => option.value === localStorage.getItem(LOGIN_LANGUAGE_KEY))?.value;

    localStorage.removeItem(LOGIN_LANGUAGE_KEY);

    if (loginLanguage) {
      if (loginLanguage !== currentLanguage) i18n.changeLanguage(loginLanguage);
      if (loginLanguage !== me.language) updateUser({ variables: { id: me.id, input: { language: loginLanguage } } });
    } else if (me.language) {
      if (me.language !== currentLanguage) i18n.changeLanguage(me.language);
    } else if (currentLanguage) {
      updateUser({ variables: { id: me.id, input: { language: currentLanguage } } });
    }
  }, [data, i18n, updateUser]);

  return (
    <AuthContext.Provider
      value={{ login, sendEmailConfirmation, confirmEmail, setPassword, finishRegistration, inviteUser, setAvatar: handleChangeAvatar, avatar }}
    >
      {children}
    </AuthContext.Provider>
  );
};
